import { MetricOptionMenuItem } from "./MetricOptionText";
import WidgetRenderer from "./charts/WidgetRenderer";
import React, { useState, useEffect, useRef } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { formatDate, dateStrToObj } from "../requests/utilities/helpers";
import { formatName } from "../../../helpers/helpers";
import {
  API_calculate_employee_metric,
  API_get_employee_metrics,
  API_get_store_team_members,
  API_save_report,
  API_get_saved_reports,
  API_delete_saved_report,
  API_update_saved_reports_order,
  API_get_report_tabs,
} from "../../../constants";
import axios from "axios";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import Checkbox from "@mui/material/Checkbox";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import TabsTwo from "../requests/shared/helpers/TabsTwo";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import SavedReportsModal from "./SavedReportsModal";
import SavedReportsView from "./SavedReportsView";
import { toast } from "react-toastify";
import {
  DATE_RANGE_GROUPS,
  getBusinessWeekStartDayIndex,
  getPresetDateRange,
} from "./dateRangePresets";
import {
  createStoreInfoLookup,
  groupStoreOptionsByCompany,
} from "../../../helpers/storeOptions";

const formatCategoryMap = {
  hours: "Hours Metrics",
  percentage: "Percentage Metrics",
  number: "Count Metrics",
  dollars: "Dollar Metrics",
};

function EmployeeReportingDisplay(props) {
  const axiosConfig = getDefaultAxiosConfig();

  // Store/Location selection state
  const [selectedStores, setSelectedStores] = useState(() =>
    props.homeStore !== undefined && props.homeStore !== null
      ? [String(props.homeStore)]
      : []
  );

  // Employee selection state
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");

  // Ref to track when loading a saved report (to skip clearing employees)
  const isLoadingReportRef = useRef(false);

  // Metrics state
  const [selectedMetrics, setSelectedMetrics] = useState(["total_shifts_scheduled"]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [calculatedMetrics, setCalculatedMetrics] = useState([]);

  // Date range state
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState("");
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);

  // Data state
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [calculationStatus, setCalculationStatus] = useState("idle");
  const [downloadStatus, setDownloadStatus] = useState("notavailable");
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);

  // Save Report State
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [reportName, setReportName] = useState("");
  const [selectedTabForReport, setSelectedTabForReport] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedReports, setSavedReports] = useState([]);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [showSavedReportsDialog, setShowSavedReportsDialog] = useState(false);
  const [showManageReports, setShowManageReports] = useState(false);
  const [reportTabs, setReportTabs] = useState([]);

  // Widget type selection state
  const [selectedWidgetType, setSelectedWidgetType] = useState("line");

  // Daily vs Weekly Tabs
  const [activeTab, setActiveTab] = useState("Daily");

  // Radio options for employee reporting
  const [selectedRadioOption, setSelectedRadioOption] = useState("Default");
  const radioOptions = ["Default", "Compare by Employee", "Compare by Role", "Compare by Metric"];

  const businessWeekStartDayIndex = getBusinessWeekStartDayIndex(
    props.storeNames,
    props.homeStore
  );

  // Store info lookup for displaying store names
  const storeInfoLookup = React.useMemo(
    () => createStoreInfoLookup(props.storeNames),
    [props.storeNames]
  );

  // Group store options by company for the dropdown
  const groupedStoreOptions = React.useMemo(() => {
    if (!Array.isArray(props.storeNames)) {
      return [];
    }

    const baseOptions = props.storeNames
      .filter((store) => store && store.id !== undefined && store.id !== null)
      .map((store) => ({ store: store.id }));

    return groupStoreOptionsByCompany(baseOptions, storeInfoLookup);
  }, [props.storeNames, storeInfoLookup]);

  const showStoreCompanyHeaders = groupedStoreOptions.length > 1;

  const selectedStoreSet = React.useMemo(
    () => new Set(selectedStores),
    [selectedStores]
  );

  // Fetch metrics, saved reports, and tabs on mount
  useEffect(() => {
    fetchMetrics();
    fetchSavedReports();
    fetchReportTabs();
  }, [props.homeStore]);

  // Fetch employees when selected stores change
  useEffect(() => {
    fetchEmployees();
  }, [selectedStores]);

  // Clear employee selection when stores change (but not when loading a saved report)
  useEffect(() => {
    if (isLoadingReportRef.current) {
      isLoadingReportRef.current = false;
      return;
    }
    setSelectedEmployees([]);
  }, [selectedStores]);

  // Metrics that don't support "Compare by Role" mode
  const metricsWithoutRoleSupport = [
    "shifts_traded",
    "time_off_taken",
    "time_off_unpaid_taken",
    "time_off_pto_taken",
    "time_off_sick_taken",
    "scheduled_to_target_hours",  // Target hours are per-employee, not per-role
    "actual_to_target_hours",     // Target hours are per-employee, not per-role
    "overtime_hours",             // Overtime is calculated per-employee (hours over 40)
  ];

  // Clear incompatible metrics when switching to "Compare by Role"
  useEffect(() => {
    if (selectedRadioOption === "Compare by Role") {
      const compatibleMetrics = selectedMetrics.filter(
        (metricValue) => !metricsWithoutRoleSupport.includes(metricValue)
      );
      if (compatibleMetrics.length !== selectedMetrics.length) {
        setSelectedMetrics(compatibleMetrics);
      }
    }
  }, [selectedRadioOption]);

  const fetchEmployees = async () => {
    if (selectedStores.length === 0) return;

    setEmployeesLoading(true);
    try {
      // Fetch employees from all selected stores in parallel
      const promises = selectedStores.map(storeId =>
        axios.get(API_get_store_team_members + storeId, axiosConfig)
      );
      const results = await Promise.all(promises);

      // Combine and tag employees with their store info
      const allEmployees = [];
      results.forEach((res, index) => {
        const storeId = selectedStores[index];
        const storeInfo = storeInfoLookup.get(Number(storeId));
        const storeName = storeInfo?.store_name || `Store ${storeId}`;

        (res.data || [])
          .filter(emp => !emp.is_hidden)
          .forEach(emp => {
            allEmployees.push({
              ...emp,
              store_id: storeId,
              store_name: storeName,
              // Unique key for selection: "employeeId-storeId"
              selection_key: `${emp.id}-${storeId}`
            });
          });
      });

      setEmployees(allEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const res = await axios.get(API_get_employee_metrics, {
        params: { store_id: props.homeStore },
        ...axiosConfig,
      });
      // Backend wraps response in redux.payload
      const metrics = res.data?.redux?.payload || res.data || [];
      setAvailableMetrics(metrics);
    } catch (error) {
      console.error("Error fetching employee metrics:", error);
      setAvailableMetrics([]);
    }
  };

  const fetchReportTabs = async () => {
    if (!props.homeStore) return;

    try {
      const response = await axios.get(API_get_report_tabs, {
        params: { store_id: props.homeStore },
        ...axiosConfig,
      });
      setReportTabs(response.data || []);
    } catch (error) {
      console.error("Error fetching tabs:", error);
      setReportTabs([]);
    }
  };

  const fetchSavedReports = () => {
    setSavedReportsLoading(true);
    const params = { store_id: props.homeStore };

    axios
      .get(API_get_saved_reports, { params, ...axiosConfig })
      .then((res) => {
        setSavedReports(res.data || []);
      })
      .catch((error) => {
        console.error("Error fetching saved reports:", error);
        setSavedReports([]);
      })
      .finally(() => {
        setSavedReportsLoading(false);
      });
  };

  function handleTabButtonPress(target) {
    setActiveTab(target);

    // Filter out any selectedMetrics that are not valid for this new tab
    const validMetricValuesForTab = availableMetrics
      .filter((metric) => metric[target])
      .map((metric) => metric.value);

    const filteredSelectedMetrics = selectedMetrics.filter((metricValue) =>
      validMetricValuesForTab.includes(metricValue)
    );

    if (filteredSelectedMetrics.length !== selectedMetrics.length) {
      setSelectedMetrics(filteredSelectedMetrics);
    }
  }

  function toggleCustomDateRange(forceState) {
    if (typeof forceState === "boolean") {
      setShowCustomDateRange(forceState);
      if (forceState) {
        setSelectedDateRange("custom");
      }
      return;
    }

    setShowCustomDateRange((prevState) => {
      const nextState = !prevState;
      if (nextState) {
        setSelectedDateRange("custom");
      }
      return nextState;
    });
  }

  function handleDateRangeChange(event) {
    const { value } = event.target;
    setSelectedDateRange(value);

    const { startDate, endDate, requiresCustomInput } = getPresetDateRange({
      preset: value,
      today: new Date(),
      businessWeekStartDayIndex,
    });

    if (requiresCustomInput) {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      toggleCustomDateRange(true);
      return;
    }

    if (value !== "custom" && showCustomDateRange) {
      toggleCustomDateRange(false);
    }

    if (startDate && endDate) {
      setSelectedStartDate(new Date(startDate));
      setSelectedEndDate(new Date(endDate));
    } else {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  }

  const dateRangeGroups = DATE_RANGE_GROUPS;

  const dateRangeMenuItems = React.useMemo(() => {
    return dateRangeGroups.reduce((items, group) => {
      items.push(
        <MenuItem
          key={`${group.label}-header`}
          disabled
          sx={{ fontWeight: "bold", opacity: 1 }}
        >
          {group.label}
        </MenuItem>
      );

      group.options.forEach((option) => {
        items.push(
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              backgroundColor:
                selectedDateRange === option.value ? "#f3e8ff" : "inherit",
            }}
          >
            {option.label}
          </MenuItem>
        );
      });

      return items;
    }, []);
  }, [dateRangeGroups, selectedDateRange]);

  let compareEmployeesChecked = selectedRadioOption === "Compare by Employee";
  let compareRolesChecked = selectedRadioOption === "Compare by Role";

  useEffect(() => {
    // Clear out previous calculation results
    setData([]);
    setRawData([]);
    setSummaryData([]);
    setEmployeeData([]);
    setRoleData([]);
    setCalculatedMetrics([]);
    setCalculationStatus("idle");
    setDownloadStatus("notavailable");
  }, [selectedRadioOption, activeTab]);

  const handleStoreChange = (event) => {
    const {
      target: { value },
    } = event;

    const rawValue = Array.isArray(value)
      ? value
      : typeof value === "string"
      ? value.split(",")
      : [value];

    const normalizedValue = rawValue.map((item) => String(item));

    if (normalizedValue.includes("all")) {
      if (selectedStores.length === props.storeNames.length) {
        setSelectedStores([]);
      } else {
        const allStoreIds = props.storeNames
          .filter((store) => store && store.id !== undefined && store.id !== null)
          .map((store) => String(store.id));
        setSelectedStores(allStoreIds);
      }
      return;
    }

    const filteredValue = normalizedValue.filter((item) => item !== "");
    setSelectedStores(filteredValue);
  };

  const handleEmployeeChange = (event) => {
    const {
      target: { value },
    } = event;

    const rawValue = Array.isArray(value)
      ? value
      : typeof value === "string"
      ? value.split(",")
      : [value];

    const normalizedValue = rawValue.map((item) => String(item));

    if (normalizedValue.includes("all")) {
      if (selectedEmployees.length === employees.length) {
        setSelectedEmployees([]);
      } else {
        // Use selection_key for composite employee-store selection
        const allEmployeeKeys = employees.map((emp) => emp.selection_key);
        setSelectedEmployees(allEmployeeKeys);
      }
      return;
    }

    const filteredValue = normalizedValue.filter((item) => item !== "");
    setSelectedEmployees(filteredValue);
  };

  const handleMetricChange = (event) => {
    const {
      target: { value },
    } = event;
    const metrics = typeof value === "string" ? value.split(",") : value;
    setSelectedMetrics(metrics);
  };

  useEffect(() => {
    setCalculationStatus("idle");
  }, [selectedEmployees, selectedMetrics, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (calculationStatus === "completed") {
      setDownloadStatus("available");
    } else {
      setDownloadStatus("notavailable");
    }
  }, [calculationStatus]);

  const calculateMetric = () => {
    setCalculationStatus("loading");

    // Convert selection keys from "empId-storeId" to "empId:storeId" format for API
    const employeeStoreParams = selectedEmployees.map(key => {
      const [empId, storeId] = String(key).split('-');
      return `${empId}:${storeId}`;
    }).join(",");

    const params = {
      selectedEmployees: employeeStoreParams,
      selectedMetrics: selectedMetrics.join(","),
      selectedStartDate: formatDate(selectedStartDate),
      selectedEndDate: formatDate(selectedEndDate),
      store_id: props.homeStore,
      activeTab: activeTab,
      selectedRadioOption: selectedRadioOption,
    };

    axios
      .get(API_calculate_employee_metric, { params: params, ...axiosConfig })
      .then((res) => {
        const payload = res.data.redux.payload;
        const newData = (payload.agg_data || []).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        const newRawData = (payload.raw_data || []).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        const newSummaryData = payload.summary_data || [];
        const newEmployeeData = payload.employee_data || [];
        const newRoleData = payload.role_data || [];

        setData(newData);
        setRawData(newRawData);
        setSummaryData(newSummaryData);
        setEmployeeData(newEmployeeData);
        setRoleData(newRoleData);
        setCalculatedMetrics([...selectedMetrics]);
        setCalculationStatus("completed");
      })
      .catch((error) => {
        console.error(error);
        setCalculationStatus("idle");
      });
  };

  function getMetricInfo(metricValue) {
    if (!metricValue) {
      return { label: "N/A", format: "N/A" };
    }

    if (!Array.isArray(availableMetrics)) {
      return { label: metricValue, format: "number" };
    }

    const metric = availableMetrics.find((m) => m.value === metricValue);

    if (metric) {
      return {
        label: metric.label,
        format: metric.format,
      };
    }

    return { label: "N/A", format: "N/A" };
  }

  function getMetricLabel(metricValue) {
    return getMetricInfo(metricValue).label.replace(/\s+/g, "_");
  }

  function getDetailedExportLabel() {
    if (activeTab === "Weekly") return "Weekly Detail";
    return "Daily Detail";
  }

  function aggregateRowsAcrossTimePeriod(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const timeFieldKeys = new Set([
      "date",
      "qh_num",
      "day_of_week",
      "week_start",
      "week_end",
      "week",
      "month",
      "year",
    ]);
    const selectedMetricSet = new Set(selectedMetrics);
    const metricFormats = selectedMetrics.reduce((acc, metric) => {
      acc[metric] = getMetricInfo(metric).format;
      return acc;
    }, {});

    const groupedRows = new Map();
    rows.forEach((row) => {
      if (!row || typeof row !== "object") return;

      const dimensionEntries = Object.entries(row).filter(
        ([key]) => !timeFieldKeys.has(key) && !selectedMetricSet.has(key)
      );
      const groupKey =
        dimensionEntries.map(([key, value]) => `${key}:${String(value)}`).join("|") ||
        "__all__";

      if (!groupedRows.has(groupKey)) {
        const seed = Object.fromEntries(dimensionEntries);
        seed.period_start = formatDate(selectedStartDate);
        seed.period_end = formatDate(selectedEndDate);
        seed.aggregation_level = "time_period";
        seed.__metric_counts = {};
        selectedMetrics.forEach((metric) => {
          seed[metric] = 0;
          seed.__metric_counts[metric] = 0;
        });
        groupedRows.set(groupKey, seed);
      }

      const aggregateRow = groupedRows.get(groupKey);
      selectedMetrics.forEach((metric) => {
        const numericValue = Number(row[metric]);
        if (Number.isNaN(numericValue)) return;
        aggregateRow[metric] += numericValue;
        aggregateRow.__metric_counts[metric] += 1;
      });
    });

    return Array.from(groupedRows.values()).map((row) => {
      const normalizedRow = { ...row };
      selectedMetrics.forEach((metric) => {
        if (
          metricFormats[metric] === "percentage" &&
          normalizedRow.__metric_counts[metric] > 0
        ) {
          normalizedRow[metric] =
            normalizedRow[metric] / normalizedRow.__metric_counts[metric];
        }
      });
      delete normalizedRow.__metric_counts;
      return normalizedRow;
    });
  }

  function handleDownload(mode) {
    setDownloadStatus("downloading");
    const rowsForDownload =
      mode === "aggregated"
        ? aggregateRowsAcrossTimePeriod(rawData)
        : rawData;
    const csvData = objectToCSV(rowsForDownload);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const fileName = `employee_${selectedMetrics
      .map(getMetricLabel)
      .join("_")}_${formatDate(selectedStartDate)}_to_${formatDate(
      selectedEndDate
    )}_${mode}.csv`;

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
    setDownloadStatus("completed");
  }

  function handleDownloadMenuOpen(event) {
    setDownloadMenuAnchorEl(event.currentTarget);
  }

  function handleDownloadMenuClose() {
    setDownloadMenuAnchorEl(null);
  }

  function handleDownloadMenuSelect(mode) {
    handleDownloadMenuClose();
    handleDownload(mode);
  }

  function objectToCSV(obj) {
    if (!obj || obj.length === 0) return "";
    const header = Object.keys(obj[0]).join(",");
    const rows = obj.map((row) => Object.values(row).join(","));
    return [header, ...rows].join("\n");
  }

  // Save Report Functions
  const handleSaveReport = () => {
    if (!checkIfSubmitValid() || calculationStatus !== "completed") {
      return;
    }
    setShowSaveDialog(true);
  };

  const handleSaveReportConfirm = () => {
    if (!reportName.trim()) {
      return;
    }

    setSaveStatus("saving");

    // Get employee names for display purposes
    const selectedEmployeeNames = employees
      .filter(emp => selectedEmployees.includes(emp.selection_key))
      .map(emp => `${emp.first_name} ${emp.last_name}`);

    const reportData = {
      name: reportName.trim(),
      report_type: "employee",
      selected_employees: selectedEmployees,  // Keep composite keys (empId-storeId) as-is
      selected_employee_names: selectedEmployeeNames,  // Store names for display
      selected_stores: selectedStores,
      selected_metrics: selectedMetrics,
      selected_date_range: selectedDateRange,
      custom_start_date:
        selectedDateRange === "custom" ? formatDate(selectedStartDate) : null,
      custom_end_date:
        selectedDateRange === "custom" ? formatDate(selectedEndDate) : null,
      active_tab: activeTab,
      selected_radio_option: selectedRadioOption,
      widget_type: selectedWidgetType,
      tab_id: selectedTabForReport,
      store_id: props.homeStore,
    };

    axios
      .post(API_save_report, reportData, axiosConfig)
      .then((res) => {
        setSaveStatus("completed");
        setReportName("");
        setSelectedTabForReport(null);
        setShowSaveDialog(false);
        fetchSavedReports();
        toast.success("Report saved successfully!", { autoClose: 3000 });
      })
      .catch((error) => {
        console.error("Error saving report:", error);
        setSaveStatus("error");
        toast.error("Failed to save report", { autoClose: 3000 });
      });
  };

  const handleDeleteSavedReport = async (reportId) => {
    const params = { store_id: props.homeStore };

    toast.loading("Deleting report...", { autoClose: false });

    try {
      await axios.delete(API_delete_saved_report + "/" + reportId, {
        params,
        ...axiosConfig,
      });
      setSavedReports((prevReports) =>
        prevReports.filter((report) => report.id !== reportId)
      );
      await fetchReportTabs();

      toast.dismiss();
      toast.success("Report deleted successfully!", { autoClose: 3000 });
    } catch (error) {
      console.error("Error deleting saved report:", error);
      toast.dismiss();
      toast.error("Failed to delete report", { autoClose: 3000 });
    }
  };

  const handleOpenSavedReportsDialog = () => {
    setShowManageReports(true);
  };

  const handleGoBack = () => {
    setShowManageReports(false);
  };

  const handleReorderReports = (result) => {
    if (!result.destination) return;

    const reorderedReports = Array.from(savedReports);
    const [movedReport] = reorderedReports.splice(result.source.index, 1);
    reorderedReports.splice(result.destination.index, 0, movedReport);

    setSavedReports(reorderedReports);

    const reportOrders = reorderedReports.map((report, index) => ({
      id: report.id,
      order: index,
    }));

    const updateData = {
      store_id: props.homeStore,
      report_orders: reportOrders,
    };

    axios
      .post(API_update_saved_reports_order, updateData, axiosConfig)
      .then((res) => {
        console.log("Report order updated successfully");
      })
      .catch((error) => {
        console.error("Error updating report order:", error);
        fetchSavedReports();
      });
  };

  const handleLoadSavedReport = (report) => {
    const storedEmployees = Array.isArray(report.selected_employees)
      ? report.selected_employees.map((emp) => String(emp))
      : [];

    // Extract unique store IDs from composite employee keys (empId-storeId format)
    const storeIdsFromEmployees = [
      ...new Set(
        storedEmployees
          .filter((key) => key.includes("-"))
          .map((key) => key.split("-")[1])
      ),
    ];

    // Set flag to prevent useEffect from clearing employees when stores change
    if (storeIdsFromEmployees.length > 0) {
      isLoadingReportRef.current = true;
      setSelectedStores(storeIdsFromEmployees);
    }

    setSelectedEmployees(storedEmployees);
    setSelectedMetrics(report.selected_metrics || []);
    setSelectedDateRange(report.selected_date_range || "");
    setActiveTab(report.active_tab || "Daily");
    setSelectedRadioOption(report.selected_radio_option || "Default");
    setSelectedWidgetType(report.widget_type || "line");

    if (
      report.selected_date_range === "custom" &&
      report.custom_start_date &&
      report.custom_end_date
    ) {
      setSelectedStartDate(dateStrToObj(report.custom_start_date));
      setSelectedEndDate(dateStrToObj(report.custom_end_date));
      setShowCustomDateRange(true);
    } else if (
      report.selected_date_range &&
      report.selected_date_range !== "custom"
    ) {
      handleDateRangeChange({ target: { value: report.selected_date_range } });
      setShowCustomDateRange(false);
    }

    setShowManageReports(false);
  };

  const checkIfTypesValid = (compareMetrics) => {
    if (!Array.isArray(availableMetrics) || availableMetrics.length === 0) return true;
    if (compareMetrics.length === 0) return true;
    const firstMetricFormat = availableMetrics.find(
      (m) => m.value === compareMetrics[0]
    )?.format;
    const areMetricsSameFormat = compareMetrics.every(
      (metric) =>
        availableMetrics.find((m) => m.value === metric)?.format ===
        firstMetricFormat
    );
    return areMetricsSameFormat;
  };

  const areMetricsSameFormat = checkIfTypesValid(selectedMetrics);

  const checkIfSubmitValid = () => {
    let valid = true;
    if (selectedEmployees.length === 0 || selectedMetrics.length === 0) {
      valid = false;
    } else if (
      !selectedStartDate ||
      !selectedEndDate ||
      selectedStartDate > selectedEndDate
    ) {
      valid = false;
    } else if (!areMetricsSameFormat) {
      valid = false;
    } else if ((compareEmployeesChecked || compareRolesChecked) && selectedMetrics.length > 1) {
      valid = false;
    }
    return valid;
  };

  // Calculate number of weeks in the date range for Weekly averages
  // Note: backend uses exclusive end_date (date__lt=end_date), so don't add 1
  const getNumWeeks = () => {
    if (!selectedStartDate || !selectedEndDate) return 1;
    const start = new Date(selectedStartDate);
    const end = new Date(selectedEndDate);
    const diffTime = Math.abs(end - start);
    const numDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.ceil(numDays / 7));
  };

  const numWeeks = getNumWeeks();

  // Daily: use backend average (number of data points)
  // Weekly: use total / number of weeks
  const calculateDisplayAverage = (data) => {
    if (activeTab === "Weekly" && data.total != null) {
      return data.total / numWeeks;
    }
    return data.average;
  };

  const enrichedSummaryData = summaryData.map((data) => {
    const metricInfo = getMetricInfo(data.metric);
    return {
      ...data,
      label: metricInfo.label,
      format: metricInfo.format,
      displayAverage: calculateDisplayAverage(data),
    };
  });

  const enrichedEmployeeData = employeeData.map((data) => {
    const metricInfo = getMetricInfo(data.metric);
    return {
      ...data,
      label: metricInfo.label,
      format: metricInfo.format,
      displayAverage: calculateDisplayAverage(data),
    };
  });

  const enrichedRoleData = roleData.map((data) => {
    const metricInfo = getMetricInfo(data.metric);
    return {
      ...data,
      label: metricInfo.label,
      format: metricInfo.format,
      displayAverage: calculateDisplayAverage(data),
    };
  });

  dayjs.extend(updateLocale);
  dayjs.updateLocale("en", {
    weekStart: businessWeekStartDayIndex,
  });

  const metricsForDropdown = Array.isArray(availableMetrics)
    ? availableMetrics.filter((metric) => {
        // Filter by active tab
        if (!metric[activeTab]) return false;
        // Filter out metrics that don't support role comparison
        if (selectedRadioOption === "Compare by Role" && metricsWithoutRoleSupport.includes(metric.value)) {
          return false;
        }
        return true;
      })
    : [];

  const groupedMetrics = metricsForDropdown.reduce((acc, metric) => {
    const category = formatCategoryMap[metric.format] || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(metric);
    return acc;
  }, {});

  const metricOptions = Object.entries(groupedMetrics).flatMap(
    ([category, metrics]) => [
      { type: "header", label: category },
      ...metrics.map((m) => ({ type: "metric", ...m })),
    ]
  );

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };

  // Get employee name by ID
  const getEmployeeName = (empId) => {
    const emp = employees.find((e) => String(e.id) === String(empId));
    if (emp) {
      return `${emp.first_name} ${emp.last_name}`;
    }
    return `Employee ${empId}`;
  };

  // If showing manage reports, render the SavedReportsView
  if (showManageReports) {
    return (
      <SavedReportsView
        savedReports={savedReports}
        savedReportsLoading={savedReportsLoading}
        storeNames={props.storeNames}
        availableMetrics={availableMetrics}
        onDeleteReport={handleDeleteSavedReport}
        onReorderReports={handleReorderReports}
        onLoadReport={handleLoadSavedReport}
        showLoadButton={true}
        title="Saved Reports"
        description="Load a saved report to populate the reporting form. You can also drag and drop to reorder reports."
        onBack={handleGoBack}
        backButtonText="Back to Employee Analytics"
        homeStore={props.homeStore}
        onRefreshReports={fetchSavedReports}
        tabs={reportTabs}
        onRefreshTabs={fetchReportTabs}
        isEmployeeReport={true}
        employees={employees}
      />
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div>
        <TopBarDisplayTitleNoLocationSelect
          title={props.analyticsSwitcher ? "Analytics" : "Employee Analytics"}
          titleExtra={props.analyticsSwitcher}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="mt-6 px-6 flex items-center space-x-16">
          <div className="w-[400px]">
            <TabsTwo
              activeTab={activeTab}
              handleTabButtonPress={handleTabButtonPress}
              tab1="Daily"
              tab2="Weekly"
            />
          </div>
          <div className="flex items-center space-x-6">
            {radioOptions.map((option) => (
              <div
                key={option}
                className="flex items-center cursor-pointer"
                onClick={() => {
                  if (
                    option !== "Default" &&
                    option !== "Compare by Metric" &&
                    selectedMetrics.length > 1
                  ) {
                    setSelectedMetrics([selectedMetrics[0]]);
                  }
                  setSelectedRadioOption(option);
                }}
              >
                <div
                  className={`
                    w-5 h-5 rounded-full border-2
                    ${
                      selectedRadioOption === option
                        ? "border-violet-700 bg-violet-700"
                        : "border-gray-300"
                    }
                    relative flex items-center justify-center
                  `}
                >
                  {selectedRadioOption === option && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <span className="ml-2 text-sm text-gray-700">{option}</span>
              </div>
            ))}
          </div>
        </div>
        {props.onOpenAIInsights && (
          <div className="mt-6 px-6">
            <button
              onClick={props.onOpenAIInsights}
              title="Chat with reports"
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-violet-700 hover:text-violet-700 transition-colors"
            >
              <AutoAwesomeIcon style={{ fontSize: 18 }} />
              <span>Chat with reports</span>
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-4 px-6 mt-6">
        <div className="w-[220px]">
          <div>
            <h4 className="text-slate-500 text-sm">
              Locations{selectedStores.length > 1 && ` (${selectedStores.length})`}
            </h4>
          </div>
          <div className="mt-1.5">
            <Select
              label="Locations"
              multiple
              value={selectedStores}
              onChange={handleStoreChange}
              input={<OutlinedInput />}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                paddingX: "15px",
                height: "40px",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
              renderValue={(selected) => {
                const selectedSet = new Set(selected);
                const selectedNames = (props.storeNames || [])
                  .filter((store) => selectedSet.has(String(store.id)))
                  .map((store) => store.store_name);

                if (selectedNames.length === 0) {
                  return "Select locations...";
                } else {
                  return selectedNames.join(", ");
                }
              }}
              className="w-full"
            >
              <MenuItem value="all">
                <Checkbox
                  checked={
                    props.storeNames?.length > 0 &&
                    selectedStores.length === props.storeNames.length
                  }
                  indeterminate={
                    selectedStores.length > 0 &&
                    selectedStores.length < (props.storeNames?.length || 0)
                  }
                  sx={{
                    color: "#6d28d9",
                    "&.Mui-checked": {
                      color: "#6d28d9",
                    },
                    "&.MuiCheckbox-indeterminate": {
                      color: "#6d28d9",
                    },
                  }}
                />
                <ListItemText
                  primary={
                    selectedStores.length === props.storeNames?.length
                      ? "Deselect All"
                      : "Select All"
                  }
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {groupedStoreOptions.flatMap((group) => {
                const items = [];
                if (showStoreCompanyHeaders) {
                  items.push(
                    <MenuItem
                      key={`header-${group.companyId || group.companyName}`}
                      disabled
                      sx={{ fontWeight: "bold", opacity: 1 }}
                    >
                      {group.companyName}
                    </MenuItem>
                  );
                }
                group.options.forEach((option) => {
                  const storeId = option.store;
                  const storeInfo = storeInfoLookup.get(storeId);
                  if (!storeInfo) return;
                  const storeValue = String(storeId);
                  items.push(
                    <MenuItem key={`store-${storeValue}`} value={storeValue}>
                      <Checkbox
                        checked={selectedStoreSet.has(storeValue)}
                        sx={{
                          color: "#6d28d9",
                          "&.Mui-checked": {
                            color: "#6d28d9",
                          },
                        }}
                      />
                      <ListItemText primary={storeInfo.store_name} />
                    </MenuItem>
                  );
                });
                return items;
              })}
            </Select>
          </div>
        </div>

        <div className="w-[260px]">
          <div>
            <h4 className="text-slate-500 text-sm">
              Employees{selectedEmployees.length > 1 && ` (${selectedEmployees.length})`}
            </h4>
          </div>
          <div className="mt-1.5">
            <Select
              label="Employees"
              multiple
              value={selectedEmployees}
              onChange={handleEmployeeChange}
              onClose={() => setEmployeeSearchTerm("")}
              input={<OutlinedInput />}
              MenuProps={{
                autoFocus: false,
                PaperProps: {
                  style: { maxHeight: 600, minWidth: 320 },
                },
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                paddingX: "15px",
                height: "40px",
                fontSize: "14px",
                width: "100%",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
              renderValue={(selected) => {
                const selectedSet = new Set(selected);
                const showStoreName = selectedStores.length > 1;
                const selectedNames = employees
                  .filter((emp) => selectedSet.has(emp.selection_key))
                  .map((emp) => {
                    const name = `${formatName(emp.first_name)} ${formatName(emp.last_name)}`;
                    return showStoreName ? `${name} (${emp.store_name})` : name;
                  });

                if (selectedNames.length === 0) {
                  return "Select employees...";
                } else {
                  return selectedNames.join(", ");
                }
              }}
              className="w-full"
            >
              <MenuItem
                dense
                disableRipple
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  "&:hover": { backgroundColor: "white" },
                  "&.Mui-focusVisible": { backgroundColor: "white" },
                  cursor: "default",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 10,
                  padding: "8px 16px",
                  borderBottom: "1px solid #e2e8f0",
                  marginBottom: "4px",
                }}
              >
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search employees..."
                  fullWidth
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      fontSize: "14px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
              </MenuItem>
              <MenuItem value="all">
                <Checkbox
                  checked={
                    employees.length > 0 &&
                    selectedEmployees.length === employees.length
                  }
                  indeterminate={
                    selectedEmployees.length > 0 &&
                    selectedEmployees.length < employees.length
                  }
                  sx={{
                    color: "#6d28d9",
                    "&.Mui-checked": {
                      color: "#6d28d9",
                    },
                    "&.MuiCheckbox-indeterminate": {
                      color: "#6d28d9",
                    },
                  }}
                />
                <ListItemText
                  primary={
                    selectedEmployees.length === employees.length
                      ? "Deselect All"
                      : "Select All"
                  }
                  primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                />
              </MenuItem>
              {employees
                .filter((emp) => {
                  if (!employeeSearchTerm) return true;
                  const searchLower = employeeSearchTerm.toLowerCase();
                  const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
                  const storeName = emp.store_name?.toLowerCase() || "";
                  return fullName.includes(searchLower) || storeName.includes(searchLower);
                })
                .map((emp) => {
                  const name = `${formatName(emp.first_name)} ${formatName(emp.last_name)}`;
                  const displayName = selectedStores.length > 1 ? `${name} (${emp.store_name})` : name;
                  return (
                    <MenuItem key={emp.selection_key} value={emp.selection_key}>
                      <Checkbox
                        checked={selectedEmployees.includes(emp.selection_key)}
                        sx={{
                          color: "#6d28d9",
                          "&.Mui-checked": {
                            color: "#6d28d9",
                          },
                        }}
                      />
                      <ListItemText primary={displayName} />
                    </MenuItem>
                  );
                })}
            </Select>
          </div>
        </div>

        <div className="w-[260px]">
          <div>
            <h4 className="text-slate-500 text-sm">
              Metrics{selectedRadioOption === "Compare by Metric" && selectedMetrics.length > 1 && ` (${selectedMetrics.length})`}
            </h4>
          </div>
          <div className="mt-1.5">
            {selectedRadioOption === "Compare by Metric" ? (
              <Select
                label="Metrics"
                multiple
                value={selectedMetrics}
                onChange={handleMetricChange}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                renderValue={(selected) => {
                  const selectedMetricsLabels = availableMetrics
                    .filter((metric) => selected.includes(metric.value))
                    .map((metric) => metric.label);

                  if (selectedMetricsLabels.length <= 2) {
                    return selectedMetricsLabels.join(", ");
                  } else {
                    return `${selectedMetricsLabels[0]}, ${selectedMetricsLabels[1]}, ...`;
                  }
                }}
                className="w-full"
              >
                {metricOptions.map((item, index) =>
                  item.type === "header" ? (
                    <MenuItem
                      key={`header-${index}`}
                      disabled
                      sx={{ fontWeight: "bold", opacity: 1 }}
                    >
                      {item.label}
                    </MenuItem>
                  ) : (
                    <MetricOptionMenuItem
                      metric={item}
                      key={item.value}
                      value={item.value}
                      sx={{
                        backgroundColor: selectedMetrics.includes(item.value)
                          ? "#f3e8ff"
                          : "inherit",
                      }}
                    >
                      <Checkbox
                        checked={selectedMetrics.includes(item.value)}
                        sx={{
                          color: "#6d28d9",
                          "&.Mui-checked": { color: "#6d28d9" },
                        }}
                      />
                    </MetricOptionMenuItem>
                  )
                )}
              </Select>
            ) : (
              <Select
                fullWidth
                renderValue={(selected) => availableMetrics
                  .filter((metric) => (Array.isArray(selected) ? selected : [selected]).includes(metric.value))
                  .map((metric) => metric.label).join(", ")}
                value={selectedMetrics}
                onChange={handleMetricChange}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                {metricOptions.map((item, index) =>
                  item.type === "header" ? (
                    <MenuItem
                      key={`header-${index}`}
                      disabled
                      sx={{ fontWeight: "bold", opacity: 1 }}
                    >
                      {item.label}
                    </MenuItem>
                  ) : (
                    <MetricOptionMenuItem key={item.value} value={item.value} metric={item} />
                  )
                )}
              </Select>
            )}
          </div>
        </div>

        {showCustomDateRange ? (
          <div className="flex gap-2 flex-1 min-w-[380px]">
            <div className="flex-1 min-w-[170px]">
              <div>
                <h4 className="text-slate-500 text-sm">Start Date</h4>
              </div>
              <div className="mt-1.5">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    Text="Start Date"
                    value={selectedStartDate}
                    onChange={(newValue) => setSelectedStartDate(newValue)}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderRadius: "8px",
                          height: "45px",
                          paddingX: "25px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                    className="w-full text-center"
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="flex-1 min-w-[170px]">
              <div>
                <h4 className="text-slate-500 text-sm">End Date</h4>
              </div>
              <div className="mt-1.5">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    Text="End Date"
                    value={selectedEndDate}
                    onChange={(newValue) => setSelectedEndDate(newValue)}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderRadius: "8px",
                          height: "45px",
                          paddingX: "25px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                    className="w-full text-center"
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="flex items-end">
              <Tooltip title="Back to preset date ranges">
                <div
                  className="w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    setSelectedDateRange("");
                    setSelectedStartDate(null);
                    setSelectedEndDate(null);
                    toggleCustomDateRange(false);
                  }}
                >
                  <UndoIcon />
                </div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-[220px]">
            <div>
              <h4 className="text-slate-500 text-sm">Date Range</h4>
            </div>
            <div className="mt-1.5">
              <Select
                fullWidth
                value={selectedDateRange}
                onChange={(event) => {
                  handleDateRangeChange(event);
                }}
                input={<OutlinedInput />}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  paddingX: "15px",
                  height: "40px",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
                className="w-full"
              >
                {dateRangeMenuItems}
              </Select>
            </div>
          </div>
        )}

        <div className="w-[180px]">
          <div>
            <h4 className="text-slate-500 text-sm">Visualization</h4>
          </div>
          <div className="mt-1.5">
            <Select
              fullWidth
              value={selectedWidgetType}
              onChange={(event) => setSelectedWidgetType(event.target.value)}
              input={<OutlinedInput />}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                paddingX: "15px",
                height: "40px",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
              className="w-full"
            >
              <MenuItem value="line">Line Chart</MenuItem>
              <MenuItem value="bar">Bar Chart</MenuItem>
              <MenuItem value="pie">Pie Chart</MenuItem>
              <MenuItem value="table">Table</MenuItem>
              <MenuItem value="cards">Metric Cards</MenuItem>
            </Select>
          </div>
        </div>

        <div className="flex items-end gap-3 self-end">
          <LoadingButton
            variant="contained"
            loading={calculationStatus === "loading"}
            loadingPosition="end"
            endIcon={
              calculationStatus === "completed" ? (
                <CheckIcon />
              ) : (
                <AutoGraphIcon />
              )
            }
            onClick={() => calculateMetric()}
            disabled={!checkIfSubmitValid() || calculationStatus === "loading"}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "40px",
              backgroundColor:
                calculationStatus === "completed" ? "green" : "#6d28d9",
              "&:hover": {
                backgroundColor:
                  calculationStatus === "completed" ? "darkgreen" : "#5b21b6",
              },
            }}
          >
            {calculationStatus === "loading"
              ? "Calculating..."
              : calculationStatus === "completed"
              ? "Metric Calculated"
              : "Calculate Metric"}
          </LoadingButton>

          <LoadingButton
            variant="contained"
            loading={downloadStatus === "downloading"}
            loadingPosition="end"
            endIcon={
              downloadStatus === "completed" ? <CheckIcon /> : <DownloadIcon />
            }
            onClick={handleDownloadMenuOpen}
            disabled={
              downloadStatus === "downloading" ||
              downloadStatus === "notavailable" ||
              data.length === 0
            }
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "40px",
              backgroundColor:
                downloadStatus === "completed" ? "green" : "#6d28d9",
              "&:hover": {
                backgroundColor:
                  downloadStatus === "completed" ? "darkgreen" : "#5b21b6",
              },
            }}
          >
            {downloadStatus === "downloading"
              ? "Downloading..."
              : downloadStatus === "completed"
              ? "Download Completed"
              : "Download Data"}
          </LoadingButton>
        </div>

      </div>
      <div className="flex items-center px-6 mt-4">
        <div className="flex items-center space-x-3">
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSaveReport}
            disabled={!checkIfSubmitValid() || calculationStatus !== "completed"}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "32px",
              borderColor: "#6d28d9",
              color: "#6d28d9",
              "&:hover": {
                borderColor: "#5b21b6",
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            Save Report
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenSavedReportsDialog}
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              height: "32px",
              borderColor: "#6d28d9",
              color: "#6d28d9",
              "&:hover": {
                borderColor: "#5b21b6",
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            View Saved{" "}
            {savedReportsLoading ? (
              <span className="inline-flex items-center">
                <svg
                  className="animate-spin ml-1.5 h-3 w-3 text-violet-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </span>
            ) : (
              `(${savedReports.length})`
            )}
          </Button>
        </div>
      </div>

      <Menu
        anchorEl={downloadMenuAnchorEl}
        open={Boolean(downloadMenuAnchorEl)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={() => handleDownloadMenuSelect("detailed")}>
          {getDetailedExportLabel()}
        </MenuItem>
        <MenuItem onClick={() => handleDownloadMenuSelect("aggregated")}>
          Aggregate Time Period
        </MenuItem>
      </Menu>

      {calculationStatus === "completed" && (
        <div>
          {compareEmployeesChecked ? (
            <div className="mt-6 px-6 flex flex-wrap gap-x-8 gap-y-4">
              {enrichedEmployeeData.map((empData, index) => (
                <div
                  key={index}
                  className="p-4 border border-slate-200 rounded-lg min-w-[180px]"
                >
                  <p className="text-xs text-slate-900">{empData.employee}</p>
                  {empData.format === "hours" || empData.format === "number" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">
                          {activeTab === "Daily" ? "Daily Avg" : "Weekly Avg"}
                        </p>
                        <p className="text-lg text-slate-900">
                          {(empData.displayAverage || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {(empData.total || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) : empData.format === "dollars" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">
                          {activeTab === "Daily" ? "Daily Avg" : "Weekly Avg"}
                        </p>
                        <p className="text-lg text-slate-900">
                          ${(empData.displayAverage || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          ${(empData.total || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                  ) : empData.format === "percentage" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {((empData.total || 0) * 100).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              ))}
            </div>
          ) : compareRolesChecked ? (
            <div className="mt-6 px-6 flex flex-wrap gap-x-8 gap-y-4">
              {enrichedRoleData.map((roleItem, index) => (
                <div
                  key={index}
                  className="p-4 border border-slate-200 rounded-lg min-w-[180px]"
                >
                  <p className="text-xs text-slate-900">{roleItem.role}</p>
                  {roleItem.format === "hours" || roleItem.format === "number" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">
                          {activeTab === "Daily" ? "Daily Avg" : "Weekly Avg"}
                        </p>
                        <p className="text-lg text-slate-900">
                          {(roleItem.displayAverage || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {(roleItem.total || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) : roleItem.format === "dollars" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">
                          {activeTab === "Daily" ? "Daily Avg" : "Weekly Avg"}
                        </p>
                        <p className="text-lg text-slate-900">
                          ${(roleItem.displayAverage || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          ${(roleItem.total || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                  ) : roleItem.format === "percentage" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {((roleItem.total || 0) * 100).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 px-6 flex flex-wrap gap-x-8 gap-y-4">
              {enrichedSummaryData.map((metric, index) => (
                <div
                  key={index}
                  className="p-4 border border-slate-200 rounded-lg min-w-[180px]"
                >
                  <p className="text-xs text-slate-900">{metric.label}</p>
                  {metric.format === "hours" || metric.format === "number" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">
                          {activeTab === "Daily" ? "Daily Avg" : "Weekly Avg"}
                        </p>
                        <p className="text-lg text-slate-900">
                          {(metric.displayAverage || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {(metric.total || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) : metric.format === "dollars" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">
                          {activeTab === "Daily" ? "Daily Avg" : "Weekly Avg"}
                        </p>
                        <p className="text-lg text-slate-900">
                          ${(metric.displayAverage || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          ${(metric.total || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                  ) : metric.format === "percentage" ? (
                    <div className="mt-2 flex space-x-8">
                      <div>
                        <p className="text-xxs text-slate-500">Total</p>
                        <p className="text-lg text-slate-900">
                          {((metric.total || 0) * 100).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="min-h-[430px] h-100 mb-5 pt-6 xl:pt-2 pb-5 w-full">
        {!areMetricsSameFormat ? (
          <div className="flex items-center min-h-[430px] justify-center h-full">
            <h3 className="text-center text-xl text-slate-500">
              Please select metrics of the same data type.
            </h3>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center min-h-[430px] justify-center h-full">
            {calculationStatus === "completed" ? (
              <h3 className="text-center text-xl text-slate-500">
                No data to show :(
              </h3>
            ) : checkIfSubmitValid() ? (
              <h3 className="text-center text-xl text-slate-500">
                Press Calculate Metric to see results.
              </h3>
            ) : (
              <h3 className="text-center text-xl text-slate-500">
                Select valid parameters and then press Calculate Metric to see
                results.
              </h3>
            )}
          </div>
        ) : data && summaryData && calculationStatus === "completed" ? (
          <div>
            <WidgetRenderer
              widgetType={selectedWidgetType}
              activeTab={activeTab}
              intradayData={[]}
              data={data}
              rawData={rawData}
              selectedMetrics={calculatedMetrics}
              metrics={availableMetrics}
              compareLocations={compareEmployeesChecked || compareRolesChecked}
              compareDow={false}
              summaryData={enrichedSummaryData}
              locationData={compareRolesChecked ? enrichedRoleData : enrichedEmployeeData}
              isDashboard={false}
            />
          </div>
        ) : (
          <div className="flex items-center min-h-[430px] justify-center h-full">
            {checkIfSubmitValid() ? (
              <h3 className="text-center text-xl text-slate-500">
                Press Calculate Metric to see results.
              </h3>
            ) : (
              <h3 className="text-center text-xl text-slate-500">
                Select valid parameters and then press Calculate Metric to see
                results.
              </h3>
            )}
          </div>
        )}
      </div>

      {/* Save Report Modal */}
      <Modal
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        aria-labelledby="save-report-modal"
        aria-describedby="save-report-description"
      >
        <Box sx={modalStyle}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setShowSaveDialog(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <SaveIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Save Employee Report
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 text-sm mb-4">
                Enter a name for this report to save it for future use and add
                it to your dashboard.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Report Name
              </label>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a report name"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "5px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />

              {reportTabs.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Organize in Tab (Optional)
                  </label>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedTabForReport || ""}
                    onChange={(e) =>
                      setSelectedTabForReport(e.target.value || null)
                    }
                    displayEmpty
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      padding: "5px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>No tab (All Reports)</em>
                    </MenuItem>
                    {reportTabs.map((tab) => (
                      <MenuItem key={tab.id} value={tab.id}>
                        {tab.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReportConfirm}
                disabled={!reportName.trim() || saveStatus === "saving"}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                  ${
                    reportName.trim() && saveStatus !== "saving"
                      ? "bg-violet-700 text-white hover:bg-violet-800"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {saveStatus === "saving" ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Saved Reports Modal */}
      <SavedReportsModal
        open={showSavedReportsDialog}
        onClose={() => setShowSavedReportsDialog(false)}
        savedReports={savedReports}
        savedReportsLoading={savedReportsLoading}
        storeNames={props.storeNames}
        availableMetrics={availableMetrics}
        onDeleteReport={handleDeleteSavedReport}
        onReorderReports={handleReorderReports}
        onLoadReport={handleLoadSavedReport}
        showLoadButton={true}
      />
    </div>
  );
}

export default EmployeeReportingDisplay;
